import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Award, 
  BadgeEuro, 
  Plane, 
  Languages, 
  Sparkles,
  ArrowRight,
  ChevronRight,
  Users,
  Star,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Header Component
const Header = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky-header">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-semibold text-primary">MED CONNECT</span>
              <span className="text-accent font-light ml-1">ALBANIA</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Services
            </a>
            <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Pricing
            </a>
            <a href="#why-us" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Why Us
            </a>
            <a href="#partners" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Partners
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/dashboard')}
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
                <Button 
                  variant="outline"
                  className="rounded-full"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button 
                  className="rounded-full bg-primary hover:bg-primary/90"
                  onClick={() => navigate('/register')}
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden py-4 border-t"
          >
            <nav className="flex flex-col gap-4">
              <a href="#services" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#pricing" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <a href="#why-us" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Why Us</a>
              <a href="#partners" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Partners</a>
              <div className="flex gap-2 pt-4 border-t">
                {isAuthenticated ? (
                  <>
                    <Button variant="outline" className="flex-1" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>Dashboard</Button>
                    <Button variant="ghost" className="flex-1" onClick={() => { logout(); setMobileMenuOpen(false); }}>Logout</Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="flex-1" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Sign In</Button>
                    <Button className="flex-1" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>Get Started</Button>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-50 via-white to-white"></div>
      
      <div className="container mx-auto px-4 md:px-8 py-16 md:py-24 lg:py-32 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-left"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-accent text-sm font-medium mb-6"
            >
              <Sparkles className="w-4 h-4" />
              European Standards, Albanian Prices
            </motion.span>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-tight text-primary mb-6"
            >
              Premium Medical Treatments in Albania
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl"
            >
              Connect with certified clinics in Tirana for dental, hair transplant, and aesthetic treatments. 
              Save up to 70% while receiving world-class care.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button 
                size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl px-8 h-12 text-base"
                onClick={() => navigate('/register')}
                data-testid="hero-cta-btn"
              >
                Request Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="rounded-full border-2 border-primary/10 hover:border-primary/30 hover:bg-primary/5 px-8 h-12 text-base"
                onClick={() => navigate('/register?role=partner')}
                data-testid="partner-cta-btn"
              >
                Become a Partner
              </Button>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="flex items-center gap-8 mt-10 pt-8 border-t border-slate-100"
            >
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-white flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">{i}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-primary">500+</strong> satisfied patients
                </p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/13275966/pexels-photo-13275966.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Tirana Cityscape"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-sm font-medium opacity-90">Tirana, Albania</p>
                <p className="text-lg font-semibold">Where Quality Meets Affordability</p>
              </div>
            </div>
            
            {/* Floating cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <BadgeEuro className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Save up to</p>
                  <p className="text-xl font-bold text-primary">70%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section className="py-20 md:py-28 bg-white" id="about">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src="https://images.pexels.com/photos/28506813/pexels-photo-28506813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Medical Consultation"
              className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-mono uppercase tracking-widest text-accent">About Us</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-primary mt-3 mb-6">
              Connecting You with Excellence
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              MED CONNECT ALBANIA bridges European patients with verified, certified medical clinics in Tirana. 
              We work exclusively with proven professionals who meet the highest standards of quality and safety.
            </p>
            
            <div className="space-y-4">
              {[
                'Verified and certified medical professionals',
                'Quality assurance and safety protocols',
                'Complete medical travel organization',
                'Multilingual support throughout your journey'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-4 h-4 text-accent" />
                  </div>
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      title: 'Dental Treatments',
      icon: '🦷',
      image: 'https://images.pexels.com/photos/4270095/pexels-photo-4270095.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      treatments: ['Dental Implants', 'All-on-4', 'Veneers', 'Full Smile Makeover'],
      description: 'World-class dental care with the latest technology and experienced specialists.',
      span: 'lg:col-span-2 lg:row-span-2'
    },
    {
      title: 'Hair Transplant',
      icon: '💇',
      image: 'https://images.pexels.com/photos/6812444/pexels-photo-6812444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      treatments: ['FUE', 'DHI'],
      description: 'Advanced hair restoration techniques for natural-looking results.',
      span: 'lg:col-span-1'
    },
    {
      title: 'Aesthetic Treatments',
      icon: '💎',
      image: 'https://images.pexels.com/photos/6129507/pexels-photo-6129507.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      treatments: ['Botox', 'Fillers', 'Facial Treatments'],
      description: 'Non-surgical and surgical cosmetic procedures for a refreshed look.',
      span: 'lg:col-span-1'
    }
  ];

  return (
    <section className="py-20 md:py-28 bg-secondary/30" id="services">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-mono uppercase tracking-widest text-accent">Our Services</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-primary mt-3">
            Premium Medical Services
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl bg-white border border-slate-100 hover:border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 ${service.span}`}
            >
              <div className="relative h-48 lg:h-full min-h-[200px] overflow-hidden">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"></div>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <span className="text-2xl mb-2 block">{service.icon}</span>
                <h3 className="text-xl md:text-2xl font-semibold mb-2">{service.title}</h3>
                <p className="text-white/80 text-sm mb-3 hidden sm:block">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.treatments.map((treatment) => (
                    <span 
                      key={treatment}
                      className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium"
                    >
                      {treatment}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const prices = [
    { treatment: 'Dental Implants', price: 'XXX', note: 'per implant' },
    { treatment: 'All-on-4', price: 'XXX', note: 'complete restoration' },
    { treatment: 'Veneers', price: 'XXX', note: 'per tooth' },
    { treatment: 'Hair Transplant (FUE)', price: 'XXX', note: 'per session' },
    { treatment: 'Botox', price: 'XXX', note: 'per area' },
    { treatment: 'Dermal Fillers', price: 'XXX', note: 'per syringe' },
  ];

  return (
    <section className="py-20 md:py-28 bg-white" id="pricing">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-mono uppercase tracking-widest text-accent">Transparent Pricing</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-primary mt-3 mb-4">
            Starting Prices
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Get an idea of our competitive pricing. Final costs are determined after medical evaluation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {prices.map((item, index) => (
            <motion.div
              key={item.treatment}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="pricing-highlight group bg-white rounded-xl border border-slate-100 hover:border-accent/30 p-6 hover:shadow-lg transition-all duration-300"
            >
              <h3 className="text-lg font-semibold text-primary mb-2">{item.treatment}</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-sm text-muted-foreground">from</span>
                <span className="text-3xl font-bold text-accent">€{item.price}</span>
              </div>
              <p className="text-sm text-muted-foreground">{item.note}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm text-muted-foreground mt-8 bg-secondary/50 rounded-lg p-4 max-w-2xl mx-auto"
        >
          * Final pricing is determined after a thorough medical evaluation. 
          Request a free estimate for personalized pricing.
        </motion.p>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUsSection = () => {
  const features = [
    { icon: ShieldCheck, title: 'Certified Clinics', desc: 'All partner clinics are verified and certified to international standards.' },
    { icon: Award, title: 'Proven Results', desc: 'Thousands of successful treatments with documented outcomes.' },
    { icon: BadgeEuro, title: 'Affordable Prices', desc: 'Save up to 70% compared to Western European prices.' },
    { icon: Plane, title: 'Medical Tourism Assistance', desc: 'Complete travel and accommodation support.' },
    { icon: Languages, title: 'Multilingual Support', desc: 'Communication in English, Italian, German, and more.' },
    { icon: Users, title: 'Dedicated Care Team', desc: 'Personal coordinator throughout your journey.' },
  ];

  return (
    <section className="py-20 md:py-28 bg-primary text-white" id="why-us">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-mono uppercase tracking-widest text-accent">Why Choose Us</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mt-3">
            The MED CONNECT Advantage
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-white/70">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Partner Section
const PartnerSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 md:py-28 bg-white" id="partners">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-mono uppercase tracking-widest text-accent">Partner Program</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-primary mt-3 mb-6">
              Join Our Referral Network
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Become a partner and earn commissions for every successful referral. 
              Share your unique referral link and track your earnings in real-time.
            </p>

            <div className="space-y-4 mb-8">
              {[
                { title: 'Earn 5-10% Commission', desc: 'On every confirmed treatment' },
                { title: 'Real-time Tracking', desc: 'Monitor your referrals and earnings' },
                { title: 'Monthly Payouts', desc: 'Reliable and transparent payments' },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ChevronRight className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <span className="font-medium text-primary">{item.title}</span>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 shadow-lg px-8 h-12"
              onClick={() => navigate('/register?role=partner')}
              data-testid="join-partner-btn"
            >
              Join Partner Network
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src="https://images.pexels.com/photos/260689/pexels-photo-260689.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Business Partnership"
              className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-primary text-white py-12 md:py-16">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-primary font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold">MED CONNECT</span>
                <span className="text-accent font-light ml-1">ALBANIA</span>
              </div>
            </div>
            <p className="text-white/70 max-w-sm">
              Connecting European patients with premium medical care in Albania. 
              Quality treatments at affordable prices.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-white/70">
              <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#why-us" className="hover:text-white transition-colors">Why Us</a></li>
              <li><a href="#partners" className="hover:text-white transition-colors">Partners</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-white/70">
              <li>info@medconnectalbania.com</li>
              <li>+355 68 608 9622</li>
              <li>Tirana, Albania</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center text-white/50 text-sm">
          <p>© {new Date().getFullYear()} MED CONNECT ALBANIA. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <PricingSection />
      <WhyUsSection />
      <PartnerSection />
      <Footer />
    </div>
  );
}
